import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { articleExcerpt, parseBlogImages, type BlogArchiveRow } from "@/lib/contentModels";
import { thumbnailImageUrl } from "@/lib/imageCdn";
import { recuerdaPrimeraFoto } from "@/lib/primeraFoto";
import { atributosDeCarga } from "@/lib/imagePriority";
import { readLocalUiState, writeLocalUiState } from "@/lib/localQueryCache";
import { usePersistWindowScroll } from "@/lib/scrollRestore";

const FALLBACK_IMAGE = "/logo-640.webp";

const PAGE_SIZE = 12;
const RETRO_BLOG_UI_KEY = "retro-blog-ui-state";

type RetroBlogUiState = {
  sortOrder: "desc" | "asc";
  yearFilter: number | "all";
  visibleCount: number;
};

const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** Timestamp para ordenar cronológicamente (mayor = más reciente). */
const getRetroBlogSortTime = (post: BlogArchiveRow): number => {
  if (post.fecha_post) {
    const d = new Date(`${post.fecha_post}T12:00:00`);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  const raw = post.link ?? post.source_archive_url ?? "";
  const m = raw.match(/\/(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?\//);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]) - 1;
    const day = m[3] ? Number(m[3]) : 1;
    const d = new Date(year, month, day);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  if (post.created_at) {
    const d = new Date(post.created_at);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return post.post_num * 86_400_000;
};

const getRetroBlogYear = (post: BlogArchiveRow): number | null => {
  if (post.fecha_post) {
    const d = new Date(`${post.fecha_post}T12:00:00`);
    if (!Number.isNaN(d.getTime())) return d.getFullYear();
  }
  const raw = post.link ?? post.source_archive_url ?? "";
  const m = raw.match(/\/(\d{4})\//);
  if (m) {
    const y = Number(m[1]);
    if (y >= 1900 && y <= 2100) return y;
  }
  if (post.created_at) {
    const d = new Date(post.created_at);
    if (!Number.isNaN(d.getTime())) return d.getFullYear();
  }
  if (post.fecha) {
    const m2 = post.fecha.match(/(\d{4})/);
    if (m2) {
      const y = Number(m2[1]);
      if (y >= 1900 && y <= 2100) return y;
    }
  }
  return null;
};

const getRetroBlogDateLabel = (post: BlogArchiveRow): string => {
  if (post.fecha_post) {
    const d = new Date(`${post.fecha_post}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    }
  }
  const raw = post.link ?? post.source_archive_url ?? "";
  const m = raw.match(/\/(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?\//);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = m[3] ? Number(m[3]) : null;
    if (year && month >= 1 && month <= 12) {
      if (day) return `${day} ${MONTHS_ES[month - 1]} ${year}`;
      if (post.created_at) {
        const parsed = new Date(post.created_at);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
        }
      }
      return `${MONTHS_ES[month - 1]} ${year}`;
    }
  }
  if (post.created_at) {
    const parsed = new Date(post.created_at);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    }
  }
  return post.fecha || "Sin fecha";
};

const EnVivo = () => {
  usePersistWindowScroll("/blogs");
  const initialUi = readLocalUiState<RetroBlogUiState>(RETRO_BLOG_UI_KEY);
  const { data: posts, isLoading } = useQuery({
    queryKey: ["blogs-retro"],
    queryFn: async () => {
      const { data, error } = await supabase.from("blogs" as never).select("*").eq("published", true);
      if (error) throw error;
      return (data ?? []) as BlogArchiveRow[];
    },
  });

  const [sortOrder, setSortOrder] = useState<"desc" | "asc">(initialUi?.sortOrder ?? "desc");
  const [yearFilter, setYearFilter] = useState<number | "all">(initialUi?.yearFilter ?? "all");
  const [visibleCount, setVisibleCount] = useState(() => {
    const raw = initialUi?.visibleCount ?? PAGE_SIZE;
    return Number.isFinite(raw) ? Math.max(PAGE_SIZE, raw) : PAGE_SIZE;
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasHydratedUiRef = useRef(false);

  const yearOptions = useMemo(() => {
    if (!posts?.length) return [] as number[];
    const set = new Set<number>();
    for (const p of posts) {
      const y = getRetroBlogYear(p);
      if (y != null) set.add(y);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [posts]);

  const processed = useMemo(() => {
    if (!posts?.length) return [];
    const list =
      yearFilter === "all" ? [...posts] : posts.filter((p) => getRetroBlogYear(p) === yearFilter);
    list.sort((a, b) => {
      const ta = getRetroBlogSortTime(a);
      const tb = getRetroBlogSortTime(b);
      return sortOrder === "desc" ? tb - ta : ta - tb;
    });
    return list;
  }, [posts, yearFilter, sortOrder]);

  useEffect(() => {
    hasHydratedUiRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    setVisibleCount(PAGE_SIZE);
  }, [yearFilter, sortOrder]);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    writeLocalUiState<RetroBlogUiState>(RETRO_BLOG_UI_KEY, {
      sortOrder,
      yearFilter,
      visibleCount: Math.max(PAGE_SIZE, visibleCount),
    });
  }, [sortOrder, yearFilter, visibleCount]);

  const visible = useMemo(() => processed.slice(0, visibleCount), [processed, visibleCount]);
  const hasMore = visibleCount < processed.length;

  // Para que la proxima visita pida ya la primera portada (src/lib/primeraFoto.ts).
  // El orden y el filtro de año tambien se recuerdan, asi que la que apuntamos
  // es la que se va a volver a pintar.
  const primeraPortada = visible[0] ? (parseBlogImages(visible[0].imagenes)[0] ?? null) : null;
  useEffect(() => {
    if (primeraPortada) recuerdaPrimeraFoto(thumbnailImageUrl(primeraPortada));
  }, [primeraPortada]);

  useEffect(() => {
    if (processed.length === 0) return;
    setVisibleCount((prev) => Math.min(Math.max(PAGE_SIZE, prev), processed.length));
  }, [processed.length]);

  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadNextChunk = useCallback(() => {
    setVisibleCount((c) => Math.min(c + PAGE_SIZE, processed.length));
  }, [processed.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isLoading) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setLoadingMore(true);
        if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
        loadMoreTimer.current = setTimeout(() => {
          loadNextChunk();
          setLoadingMore(false);
        }, 200);
      },
      { root: null, rootMargin: "400px", threshold: 0 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
    };
  }, [hasMore, isLoading, loadNextChunk, visibleCount]);

  const chipCls = (active: boolean) =>
    `h-9 rounded-md px-2 text-xs font-medium ${
      active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
    }`;

  const selectCls =
    "mt-1 min-w-[10rem] rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring";
  const backLinkCls =
    "mb-5 inline-flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-primary";

  return (
    <div className="mx-auto max-w-[80rem] px-5 py-8 md:px-9">
      <Link to="/articulos" className={backLinkCls}>
        ← Volver
      </Link>
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
        <div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">Blog retro Popistant</h1>
          <p className="mt-1 text-sm text-muted-foreground">Archivo histórico del blog original.</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end md:w-auto md:min-w-[34rem] md:self-start">
          <select
            id="year-filter"
            className="h-9 rounded-md border border-border bg-background px-3 text-xs text-foreground outline-none focus:ring-2 focus:ring-ring sm:w-[11rem]"
            value={yearFilter === "all" ? "all" : String(yearFilter)}
            onChange={(e) => {
              const v = e.target.value;
              setYearFilter(v === "all" ? "all" : Number(v));
            }}
          >
            <option value="all">Todos los años</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-border bg-muted/40 p-0.5 sm:w-[11rem]">
            <button type="button" className={chipCls(sortOrder === "desc")} onClick={() => setSortOrder("desc")}>
              Recientes
            </button>
            <button type="button" className={chipCls(sortOrder === "asc")} onClick={() => setSortOrder("asc")}>
              Antiguos
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-border bg-card">
              <div className="aspect-[4/3] rounded-t-xl bg-muted" />
              <div className="space-y-2 p-4">
                <div className="h-4 w-1/3 rounded bg-muted" />
                <div className="h-5 w-2/3 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : posts && posts.length > 0 ? (
        <>
          {processed.length === 0 ? (
            <p className="py-16 text-center text-muted-foreground">No hay entradas para el año seleccionado.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((post, idx) => {
                  const cover = parseBlogImages(post.imagenes)[0] ?? null;
                  const dateLabel = getRetroBlogDateLabel(post);
                  return (
                    <Link
                      key={post.post_num}
                      to={`/post/blog-legacy/${post.post_num}`}
                      className="group overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md"
                    >
                      {cover ? (
                        <img
                          src={thumbnailImageUrl(cover)}
                          alt={post.titulo}
                          className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          {...atributosDeCarga(idx === 0)}
                          referrerPolicy="no-referrer"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = FALLBACK_IMAGE;
                          }}
                        />
                      ) : (
                        <div className="flex aspect-[4/3] items-center justify-center bg-muted">
                          <img
                            src={FALLBACK_IMAGE}
                            alt=""
                            className="h-20 w-20 rounded-2xl object-cover shadow-sm md:h-24 md:w-24"
                            aria-hidden
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{dateLabel}</span>
                        <h3 className="mt-1 font-display text-base font-bold text-foreground">{post.titulo}</h3>
                        {post.descripcion && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{articleExcerpt(post.descripcion)}</p>}
                      </div>
                    </Link>
                  );
                })}
              </div>

              {hasMore && (
                <div ref={sentinelRef} className="flex min-h-[72px] flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
                  {loadingMore ? (
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      <span>Cargando más…</span>
                    </div>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest opacity-60">Desplázate para cargar más</span>
                  )}
                </div>
              )}

              {!hasMore && processed.length > PAGE_SIZE && (
                <p className="text-center text-xs text-muted-foreground">Has llegado al final del archivo.</p>
              )}
            </>
          )}
        </>
      ) : (
        <p className="py-16 text-center text-muted-foreground">No hay entradas retro disponibles.</p>
      )}
    </div>
  );
};

export default EnVivo;
